import { Component } from '@theme/component';
import { lockScroll, unlockScroll, calculateHeaderGroupHeight } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * ZeroWheel header (sections/zw-header.liquid, spec docs/specs/zw-header-footer.md).
 *
 * - Sets `data-scrolled` once the page has scrolled, so the home page's transparent header turns dark.
 * - Opens and closes the menu overlay on click only.
 * - Keeps `--header-height` and `--header-group-height` on <body> up to date for Horizon's components.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} menuToggle - The menu button.
 *
 * @extends {Component<Refs>}
 */
class ZwHeaderComponent extends Component {
  requiredRefs = ['menuToggle'];

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #scrollFrame = null;

  #resizeObserver = new ResizeObserver(() => this.#updateHeights());

  // Sections added to the header group later (e.g. an announcement bar in the editor) are observed too.
  #groupObserver = new MutationObserver(() => this.#observeHeaderGroup());

  connectedCallback() {
    super.connectedCallback();

    this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#onBreakpointChange);
    this.addEventListener('click', this.#onClick);
    window.addEventListener('pageshow', this.#onPageShow);

    const headerGroup = this.closest('#header-group');
    if (headerGroup) this.#groupObserver.observe(headerGroup, { childList: true });
    this.#observeHeaderGroup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.closeMenu();
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
    if (this.#scrollFrame !== null) cancelAnimationFrame(this.#scrollFrame);
    scrollContainerMediaQuery.removeEventListener('change', this.#onBreakpointChange);
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#groupObserver.disconnect();
    this.#resizeObserver.disconnect();
  }

  get isMenuOpen() {
    return this.hasAttribute('data-menu-open');
  }

  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu({ returnFocus: true });
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    if (this.isMenuOpen) return;

    // The overlay starts below the bar, which sits lower when an announcement bar is above it.
    this.style.setProperty('--zw-header-bottom', `${Math.round(this.getBoundingClientRect().bottom)}px`);
    this.setAttribute('data-menu-open', '');
    this.refs.menuToggle.setAttribute('aria-expanded', 'true');
    lockScroll(this);
    trapFocus(this);
    // On the document: after a mouse click, Safari leaves focus on <body>, outside the header.
    document.addEventListener('keydown', this.#onKeydown);
  }

  /**
   * @param {{ returnFocus?: boolean }} [options]
   */
  closeMenu({ returnFocus = false } = {}) {
    if (!this.isMenuOpen) return;

    this.removeAttribute('data-menu-open');
    this.refs.menuToggle.setAttribute('aria-expanded', 'false');
    unlockScroll(this);
    removeTrapFocus();
    document.removeEventListener('keydown', this.#onKeydown);
    if (returnFocus) this.refs.menuToggle.focus();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isMenuOpen) return;

    event.preventDefault();
    this.closeMenu({ returnFocus: true });
  };

  /**
   * Close the menu when one of its links or the cart (which sits above the open menu) is used, so an in-page
   * link or the cart drawer isn't hidden behind it.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.zw-header__cart, .zw-header__menu-link')) this.closeMenu();
  };

  /**
   * A page restored from the back/forward cache comes back as it was left; don't bring the menu back open.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.closeMenu();
  };

  #observeHeaderGroup() {
    this.#resizeObserver.disconnect();
    this.#resizeObserver.observe(this);
    const headerGroup = this.closest('#header-group');
    if (headerGroup) {
      for (const child of headerGroup.children) this.#resizeObserver.observe(child);
    }
    this.#updateHeights();
  }

  #bindScroll() {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#updateScrolled();
  }

  // The scroll container switches between .page-wrapper (≥990px) and the document.
  #onBreakpointChange = () => this.#bindScroll();

  #onScroll = () => {
    if (this.#scrollFrame !== null) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#updateScrolled();
    });
  };

  #updateScrolled() {
    this.toggleAttribute('data-scrolled', getScrollTop() > 0);
  }

  #updateHeights() {
    document.body.style.setProperty('--header-height', `${Math.round(this.offsetHeight)}px`);

    const headerGroup = this.closest('#header-group');
    if (!(headerGroup instanceof HTMLElement)) return;

    const groupHeight = calculateHeaderGroupHeight(this, headerGroup);
    document.body.style.setProperty('--header-group-height', `${Math.round(groupHeight)}px`);
  }
}

if (!customElements.get('zw-header-component')) {
  customElements.define('zw-header-component', ZwHeaderComponent);
}
